class TomoSaleCountdown extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      const endsAtTimestamp = Number(this.dataset.saleEndsAtTimestamp);
      this.endsAt =
        Number.isFinite(endsAtTimestamp) && endsAtTimestamp > 0 ? endsAtTimestamp * 1000 : Date.parse(this.dataset.saleEndsAt);
      this.days = this.querySelector('[data-countdown-days]');
      this.hours = this.querySelector('[data-countdown-hours]');
      this.minutes = this.querySelector('[data-countdown-minutes]');
      this.seconds = this.querySelector('[data-countdown-seconds]');
      this.a11yText = this.querySelector('[data-countdown-a11y]');
      this.endsAtText = this.querySelector('[data-countdown-ends-at]');
      this.expiredText = this.querySelector('[data-countdown-expired]');
      this.label = this.querySelector('.tomo-sale-countdown__label')?.textContent?.trim() || 'Sale ends in';
      this.renderLocalEndTime();
    }

    if (!Number.isFinite(this.endsAt)) {
      this.expire();
      return;
    }

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();

    if (this.tick()) {
      this.interval = window.setInterval(() => this.tick(), 1000);
    }
  }

  stop() {
    if (this.interval) {
      window.clearInterval(this.interval);
      this.interval = null;
    }
  }

  renderLocalEndTime() {
    if (!this.endsAtText) return;

    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    this.endsAtText.textContent = formatter.format(new Date(this.endsAt));
  }

  tick() {
    const remaining = this.endsAt - Date.now();

    if (remaining <= 0) {
      this.expire();
      return false;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setValue(this.days, days);
    this.setValue(this.hours, hours);
    this.setValue(this.minutes, minutes);
    this.setValue(this.seconds, seconds);

    if (this.a11yText) {
      this.a11yText.textContent = `${this.label} ${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
    }

    return true;
  }

  setValue(element, value) {
    if (!element) return;

    element.textContent = String(value).padStart(2, '0');
  }

  expire() {
    this.stop();

    if (this.expiredText) {
      this.expiredText.hidden = false;
    }

    if (this.dataset.hideSaleCardOnExpire === 'true') {
      this.hideSaleCard();
      return;
    }

    this.hidden = true;
  }

  hideSaleCard() {
    const card = this.closest('[data-tomo-sale-card]');

    if (!card) {
      this.hidden = true;
      return;
    }

    card.hidden = true;

    const section = card.closest('[data-tomo-sale-section]');
    if (!section) return;

    const visibleCards = section.querySelectorAll('[data-tomo-sale-card]:not([hidden])');
    if (visibleCards.length === 0) {
      section.hidden = true;

      const sectionBackground = section.previousElementSibling;
      if (sectionBackground?.hasAttribute('data-tomo-sale-section-background')) {
        sectionBackground.hidden = true;
      }
    }
  }
}

if (!customElements.get('tomo-sale-countdown')) {
  customElements.define('tomo-sale-countdown', TomoSaleCountdown);
}

const hydrateManagedSaleSections = () => {
  if (window.__tomoManagedSaleSectionsHydrated) return;

  const sections = Array.from(document.querySelectorAll('.limited-time-sale-collections-wrapper[id^="shopify-section-"]'));
  const staleSections = sections.filter((section) => !section.querySelector('[data-tomo-sale-source]'));

  if (!staleSections.length) return;

  window.__tomoManagedSaleSectionsHydrated = true;

  const sectionIds = staleSections
    .map((section) => section.id.replace(/^shopify-section-/, ''))
    .filter(Boolean);

  if (!sectionIds.length) return;

  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('sections', sectionIds.join(','));

  fetch(url.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Section refresh failed: ${response.status}`);
      return response.json();
    })
    .then((payload) => {
      sectionIds.forEach((sectionId) => {
        const html = payload[sectionId];
        const section = document.getElementById(`shopify-section-${sectionId}`);

        if (!section || typeof html !== 'string' || !html.includes('data-tomo-sale-section')) return;

        section.outerHTML = html;
      });
    })
    .catch(() => {
      window.__tomoManagedSaleSectionsHydrated = false;
    });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateManagedSaleSections, { once: true });
} else {
  hydrateManagedSaleSections();
}
